import { useQuery } from "@tanstack/react-query";
import {
	getCfgClassificacoes,
	listarCfgParametrosGerais,
	getMeiosPagamento,
	listarContasBaixaPagar,
} from "../control/utilitarios-service.spaceApi";
import type {
	IListarContasBaixaPagarRequest,
	IListarMeiosPagamentoRequest,
	NomeParametroGeral,
} from "../dtos/utilitarios-service.dto";

export const keys_utilitariosService = {
	base: ["space", "utilitarios-service"] as const,
	cfgParametrosGerais: (nomeParametro?: NomeParametroGeral) =>
		[...keys_utilitariosService.base, "cfg-parametros-gerais", nomeParametro ?? "todos"] as const,
	cfgClassificacoes: (descricaoTipo: string, descricaoGrupo?: string) =>
		[...keys_utilitariosService.base, "cfg-classificacao", descricaoTipo, descricaoGrupo] as const,
	meiosPagamento: (params: IListarMeiosPagamentoRequest) =>
		[...keys_utilitariosService.base, "meio-pagamento", params] as const,
	contasBaixa: (params: IListarContasBaixaPagarRequest) =>
		[...keys_utilitariosService.base, "contas-baixa", params] as const,
};

// --------------------------------- TYPES ---------------------------------
type UseCfgParametrosGeraisOptions = {
	readonly enabled?: boolean;
	readonly nomeParametro?: NomeParametroGeral;
};

// ------------------------------- FUNCTIONS -------------------------------
export function useCfgParametrosGerais({
	enabled = true,
	nomeParametro,
}: Readonly<UseCfgParametrosGeraisOptions> = {}) {
	return useQuery({
		queryKey: keys_utilitariosService.cfgParametrosGerais(nomeParametro),
		queryFn: ({ signal }) => listarCfgParametrosGerais({ nomeParametro, signal }),
		enabled,
		staleTime: 5 * 60_000,
		gcTime: 10 * 60_000,
		retry: 0,
	});
}

export function useCfgClassificacoes(descricaoTipo: string, descricaoGrupo?: string, enabled = true) {
	return useQuery({
		queryKey: keys_utilitariosService.cfgClassificacoes(descricaoTipo, descricaoGrupo),
		queryFn: ({ signal }) => getCfgClassificacoes(descricaoTipo, descricaoGrupo, signal),
		enabled,
		staleTime: 5 * 60_000,
		gcTime: 10 * 60_000,
		retry: 0,
	});
}

export function useListarMeiosPagamento(params: IListarMeiosPagamentoRequest, enabled = true) {
	return useQuery({
		queryKey: keys_utilitariosService.meiosPagamento(params),
		queryFn: ({ signal }) => getMeiosPagamento(params, signal),
		enabled,
		staleTime: 5 * 60_000,
		gcTime: 10 * 60_000,
		retry: 0,
	});
}

export function useListarContasBaixaPagar(params: IListarContasBaixaPagarRequest | null) {
	return useQuery({
		queryKey: keys_utilitariosService.contasBaixa(params ?? { id_empresa: 0, tipo_conta: "dinheiro" }),
		queryFn: ({ signal }) => {
			if (!params) return [];
			return listarContasBaixaPagar(params, signal);
		},
		enabled: params !== null,
		staleTime: 60_000,
		retry: 0,
	});
}
